'use client';

import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { getImageProps } from 'next/image';

import { cn } from '@/lib/utils';

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  isSquare?: boolean;
}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, isSquare = false, ...props }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 shrink-0 overflow-hidden',
        isSquare ? 'rounded-none' : 'rounded-full',
        className,
      )}
      {...props}
    />
  ),
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

interface AvatarImageProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> {
  width?: number;
  height?: number;
  sizes?: string;
  quality?: number;
}

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, src, width, height, sizes, quality = 80, ...props }, ref) => {
  // Use Next.js Image optimization if dimensions are provided
  const imageProps = React.useMemo(() => {
    if (src && width && height) {
      try {
        // Limit srcSet to reasonable sizes for avatars to avoid generating unnecessarily large images
        const maxSize = Math.max(width, height) * 4;

        const { props: optimizedProps } = getImageProps({
          src: src as string,
          width,
          height,
          quality,
          sizes: sizes || `${width}px`,
          alt: (props as { alt?: string }).alt || '',
        });

        // Filter srcSet to only include sizes up to maxSize
        const filteredSrcSet = optimizedProps.srcSet
          ?.split(', ')
          .filter((entry) => {
            const sizeMatch = entry.match(/(\d+)w$/);
            return sizeMatch ? parseInt(sizeMatch[1]) <= maxSize : true;
          })
          .join(', ');

        return {
          src: optimizedProps.src,
          srcSet: filteredSrcSet || optimizedProps.srcSet,
        };
      } catch {
        // Fallback to original src if optimization fails
        return { src };
      }
    }
    return { src };
  }, [src, width, height, quality, sizes, props]);

  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn('aspect-square h-full w-full', className)}
      {...props}
      {...imageProps}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

interface AvatarFallbackProps extends React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Fallback
> {
  isSquare?: boolean;
}

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, isSquare = false, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center bg-muted',
      isSquare ? 'rounded-none' : 'rounded-full',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
